import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PartyPopper, Heart, Star, Hand } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { api, errMessage } from "../lib/api";

const REACTION_ICONS = { heart: Heart, clap: Hand, star: Star };

// Family cheers — owner's dashboard view of member reactions. Parent-only:
// reactions are NEVER shown to the child. Owner can disable + clear history.
export const FamilyCheersPanel = () => {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = useCallback(() => {
    api.get("/recap/reactions").then(({ data }) => setData(data)).catch(() => setData(null));
    api.get("/recap/encouragement-summary").then(({ data }) => setSummary(data)).catch(() => setSummary(null));
  }, []);
  useEffect(load, [load]);

  if (!data) return null; // non-owners (and flag-off) simply don't see this

  const toggle = async (enabled) => {
    try {
      await api.put("/recap/reactions-settings", { enabled });
      setData({ ...data, enabled });
      toast.success(enabled ? "Family reactions on" : "Family reactions off");
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const clear = async () => {
    try {
      const { data: res } = await api.delete("/recap/reactions");
      toast.success(`Reaction history deleted (${res.deleted} removed)`);
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border p-5 space-y-3" data-testid="family-cheers-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <PartyPopper className="h-4 w-4 text-secondary" aria-hidden="true" />
          <p className="text-sm font-bold">Family cheers</p>
        </div>
        <div className="flex items-center gap-3">
          {data.reactions.length > 0 && (
            <Button variant="ghost" size="sm" className="rounded-full text-destructive h-8"
              onClick={clear} data-testid="cheers-clear-button">
              Delete history
            </Button>
          )}
          <Switch checked={data.enabled} onCheckedChange={(v) => toggle(Boolean(v))}
            aria-label="Family reactions" data-testid="cheers-toggle" />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Shared family members can send one small cheer per child each week. Cheers appear here
        for you only — nothing is ever sent to your child.
      </p>
      {data.reactions.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="cheers-empty">No cheers yet this week.</p>
      ) : (
        <div className="space-y-1.5" data-testid="cheers-list">
          {data.reactions.slice(0, 8).map((r) => {
            const Icon = REACTION_ICONS[r.reaction_key] || Heart;
            return (
              <div key={`${r.member_user_id}-${r.child_id}-${r.week_start}`}
                className="flex items-center gap-2 bg-muted/40 rounded-xl px-3 py-2 text-sm"
                data-testid={`cheer-row-${r.child_id}-${r.reaction_key}`}>
                <Icon className="h-4 w-4 text-rose-500 shrink-0" aria-hidden="true" />
                <span><b>{r.member_name}</b> sent {r.child_name} a {r.reaction_label.toLowerCase()} · week of {new Date(r.week_start).toLocaleDateString()}</span>
              </div>
            );
          })}
        </div>
      )}
      {data.thank_yous?.length > 0 && (
        <div className="space-y-1.5" data-testid="thank-yous-list">
          {data.thank_yous.slice(0, 4).map((t) => (
            <div key={t.cheer_back_id}
              className="flex items-center gap-2 bg-secondary/10 rounded-xl px-3 py-2 text-sm"
              data-testid={`thank-you-row-${t.child_id}-${t.week_start}`}>
              <Heart className="h-4 w-4 text-secondary fill-current shrink-0" aria-hidden="true" />
              <span><b>{t.child_name}</b> sent the family a thank-you heart · week of {new Date(t.week_start).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
      {summary && (
        <div className="rounded-xl border border-border p-3 space-y-2" data-testid="encouragement-summary">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-bold">This week's encouragement</p>
            <Switch checked={summary.enabled}
              onCheckedChange={async (v) => {
                try {
                  await api.put("/recap/encouragement-settings", { enabled: Boolean(v) });
                  setSummary({ ...summary, enabled: Boolean(v) });
                  toast.success(v ? "Weekly encouragement summary on" : "Weekly encouragement summary off");
                } catch (e) {
                  toast.error(errMessage(e));
                }
              }}
              aria-label="Weekly encouragement summary" data-testid="encouragement-toggle" />
          </div>
          {summary.enabled && (
            summary.children.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="encouragement-empty">
                A quiet week so far — every week is a fresh start.
              </p>
            ) : (
              summary.children.map((c) => (
                <p key={c.child_name} className="text-xs text-muted-foreground" data-testid={`encouragement-child-${c.child_name}`}>
                  {c.child_name} was cheered on by {c.cheers.map((x) => x.member_name).join(", ")} this week — lovely!
                </p>
              ))
            )
          )}
        </div>
      )}
    </div>
  );
};
